import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Stack } from '@mui/material';
import * as Yup from 'yup';

import { UserApi } from '@/api';
import { RenderFormField } from '@/components';
import { IAgency, IUser } from '@/models';
import { useAgencyStore, useToastStore, useUserStore } from '@/store';
import { ConfirmTypes } from '@/store/store.toast';
import { maskToUSNumber } from '@/utils';

interface AccountInformationFormData {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  agency: string;
  emergencyContactName?: string;
  emergencyContactPhoneNumber?: string;
}

const phoneTest = (value?: string) => !value || /^\d{10}$/.test(value);

const accountInformationSchema: Yup.ObjectSchema<AccountInformationFormData> =
  Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required('This field is required.')
      .max(50, 'First name must be 50 characters or fewer.'),
    middleName: Yup.string()
      .trim()
      .max(50, 'Middle name must be 50 characters or fewer.')
      .optional(),
    lastName: Yup.string()
      .trim()
      .required('This field is required.')
      .max(50, 'Last name must be 50 characters or fewer.'),
    email: Yup.string().default(''),
    phoneNumber: Yup.string()
      .test('phone', 'Invalid phone number.', phoneTest)
      .optional(),
    agency: Yup.string().default(''),
    emergencyContactName: Yup.string()
      .trim()
      .max(100, 'Emergency contact name must be 100 characters or fewer.')
      .optional(),
    emergencyContactPhoneNumber: Yup.string()
      .test('phone', 'Invalid phone number.', phoneTest)
      .optional(),
  });

const stripPhonePrefix = (value?: string | null) =>
  value ? value.replace(/^\+1/, '') : '';

const getAgencyName = (user?: IUser | null, fallback?: IAgency | null) => {
  if (user?.roles) {
    for (const role of user.roles) {
      if (role.agency && typeof role.agency === 'object') {
        const name = (role.agency as IAgency).name;
        if (name) return name;
      }
    }
  }
  return fallback?.name ?? '';
};

const buildDefaults = (
  user?: IUser | null,
  agencyFallback?: IAgency | null,
): AccountInformationFormData => ({
  firstName: user?.firstName ?? '',
  middleName: user?.middleName ?? '',
  lastName: user?.lastName ?? '',
  email: user?.email ?? '',
  phoneNumber: stripPhonePrefix(user?.phoneNumber),
  agency: getAgencyName(user, agencyFallback),
  emergencyContactName: user?.profile?.emergencyContact?.name ?? '',
  emergencyContactPhoneNumber: stripPhonePrefix(
    user?.profile?.emergencyContact?.phoneNumber,
  ),
});

export const AccountInformationForm: FC = () => {
  const { user, updateUserStore } = useUserStore();
  const { currentAgency } = useAgencyStore();
  const { updateToast } = useToastStore();

  const defaultValues = useMemo(
    () => buildDefaults(user, currentAgency),
    [user, currentAgency],
  );

  const methods = useForm<AccountInformationFormData>({
    mode: 'all',
    resolver: yupResolver(accountInformationSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid, isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const middleName = values.middleName?.trim();
      const phone = values.phoneNumber
        ? maskToUSNumber(values.phoneNumber)
        : null;
      const emergencyName = values.emergencyContactName?.trim();
      const emergencyPhone = values.emergencyContactPhoneNumber
        ? maskToUSNumber(values.emergencyContactPhoneNumber)
        : null;

      const hadEmergencyContact = Boolean(
        user?.profile?.emergencyContact?.name ||
          user?.profile?.emergencyContact?.phoneNumber,
      );

      const payload: Parameters<typeof UserApi.updateMe>[0] = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        middleName: middleName ? middleName : null,
        phoneNumber: phone,
      };

      if (emergencyName || emergencyPhone || hadEmergencyContact) {
        payload.profile = {
          emergencyContact: {
            name: emergencyName ? emergencyName : null,
            phoneNumber: emergencyPhone,
          },
        };
      }

      const res = await UserApi.updateMe(payload);
      updateUserStore({ user: res.data });
      reset(buildDefaults(res.data, currentAgency));
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Profile updated successfully.',
      });
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to update profile. Please try again.';
      updateToast({
        open: true,
        type: ConfirmTypes.ERROR,
        message,
      });
    }
  });

  const canSubmit = isDirty && isValid && !isSubmitting;

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <RenderFormField name="lastName" type="text" label="Last name" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <RenderFormField
                name="firstName"
                type="text"
                label="First name"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <RenderFormField
                name="middleName"
                type="text"
                label="Middle name"
              />
            </Grid>
            <Grid item xs={12}>
              <RenderFormField
                name="email"
                type="text"
                label="Email address"
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <RenderFormField
                name="phoneNumber"
                type="pattern"
                label="Phone number"
                format="(###) ###-####"
              />
            </Grid>
            <Grid item xs={12}>
              <RenderFormField
                name="agency"
                type="text"
                label="Agency"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RenderFormField
                name="emergencyContactName"
                type="text"
                label="Emergency contact name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RenderFormField
                name="emergencyContactPhoneNumber"
                type="pattern"
                label="Emergency contact phone number"
                format="(###) ###-####"
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={!canSubmit}
            >
              Save changes
            </Button>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  );
};
